import type { ReactNode } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../form/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select';
import { Text } from '@folio/components/ui/text';

export type FormSelectInputProps<TFields extends FieldValues> = {
  control: Control<TFields>;
  name: Path<TFields>;
  label?: ReactNode;
  options?: SelectOption[];
  description?: ReactNode;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
};

type SelectOption = {
  value: string;
  label: ReactNode;
  disabled?: boolean;
};

export function FormSelectInput<TFields extends FieldValues>({
  control,
  name,
  label,
  options,
  description,
  placeholder,
  required,
  disabled,
}: FormSelectInputProps<TFields>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}{' '}
            {required && (
              <Text inline tone="destructive">
                *
              </Text>
            )}
          </FormLabel>{' '}
          <Select onValueChange={field.onChange} value={field.value}>
            <FormControl>
              <SelectTrigger disabled={disabled}>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options?.map(option => (
                <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!!description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
